import { AxiosError } from 'axios';
import {
  authLockMessage,
  describeError,
  problemErrorCode,
  problemStatus,
  type AgentAuthorizationConsent,
  type AgentAuthorizationExclusionReason,
  type AgentWriteScope,
  type PersonalRedirectTarget,
} from './api';
import { ACCOUNT_LOCKED, ATTEMPTS_IN_FLIGHT, STEP_UP_INVALID, STEP_UP_RATE_LIMITED, STEP_UP_REQUIRED } from './step-up';
import { AGENT_SCOPES, SCOPE_DESCRIPTIONS, scopesUpToLevel } from './agents';

/**
 * Regras puras da tela de consentimento do MCP (`/mcp/autorizar`; change platform-mcp-server,
 * S-2.16a): classificação das recusas da API, conferência da `redirectUrl` antes de sair, níveis
 * que o Admin pode escolher e rótulos. A API revalida tudo; aqui só se decide o que a tela mostra
 * e se os botões reabrem.
 */

export const UNAVAILABLE_MESSAGE = 'Recurso indisponível nesta versão da API.';

const CODE_PREFIX = 'platform.agent_oauth.';

export type ConsentFailureKind =
  | 'invalid_client'
  | 'invalid_redirect_uri'
  | 'mcp_disabled'
  | 'request_not_yours'
  | 'reauth'
  | 'session'
  | 'step_up'
  | 'locked'
  | 'request_not_pending'
  | 'catalog_changed'
  | 'unavailable'
  | 'misconfigured'
  | 'unsafe_redirect'
  | 'terminal'
  | 'retryable';

export interface ConsentFailure {
  kind: ConsentFailureKind;
  message: string;
}

/** Mensagens próprias de cada recusa: texto fixo, nunca um valor da query. */
export const FAILURE_MESSAGES = {
  invalid_client:
    'Cliente de agente desconhecido, desabilitado ou de outro ambiente. Confira o perfil em Agentes de IA. O navegador não volta ao Hermes.',
  invalid_redirect_uri:
    'O endereço de volta não é o registrado para este perfil. Por segurança, o navegador não sai do backoffice.',
  mcp_disabled: 'O MCP está desligado neste ambiente: nenhuma concessão é aprovada agora.',
  request_not_yours:
    'Outra conta ou outra sessão não aprova o pedido: só a conta e a sessão que o abriram podem decidi-lo. Recomece pelo Hermes ou, se for o seu Claude, pelo Claude Code ou pelo claude.ai.',
  // Variante pessoal (change platform-mcp-operator-access, OA-D3): só o titular aprova (a API
  // responde `request_not_yours` a qualquer outra conta), e a tela não oferece trocar de conta.
  personal_only:
    'Só um Admin aprova a conexão de um agente do Hermes. Na sua conta, esta tela só aprova o seu próprio Claude Code.',
  // O `invalid_client` da rota pessoal: cliente pessoal inexistente, desabilitado, de outro
  // ambiente ou com o caminho do operador desligado (`PlatformMcp:Operator:Enabled=false`).
  personal_invalid_client:
    'O Claude Code da sua conta não está habilitado neste ambiente, ou o acesso do operador está desligado. Peça a um Admin em Agentes de IA. O navegador não volta ao Claude Code.',
  reauth:
    'Pedir uma concessão de agente exige login nos últimos 15 minutos, com senha e TOTP; sessão aberta por recovery code não vale. Entre de novo: você voltará a este pedido.',
  reauth_approval:
    'Aprovar uma concessão de agente exige uma sessão aberta com senha e TOTP; sessão aberta por recovery code não vale. Entre de novo com o código do autenticador: você voltará a este pedido.',
  // Só quando a recusa não chega como o 423 da conta bloqueada; nele a espera vem do `retryAfter`.
  locked:
    'Sua conta está bloqueada por tentativas erradas do código do autenticador. Tente de novo mais tarde ou peça o desbloqueio a um Admin; o pedido segue aberto até vencer, e Negar continua disponível.',
  session: 'Sua sessão não vale mais. Entre de novo: você voltará a este pedido.',
  request_not_pending:
    'Pedido vencido ou já decidido. Recomece pelo Hermes: MCP → perfil → vaptapi → Authenticate.',
  catalog_changed:
    'O catálogo de ferramentas mudou desde que a tela abriu. O pedido foi recarregado: confira e aprove de novo.',
  unavailable: UNAVAILABLE_MESSAGE,
  misconfigured:
    'A configuração do MCP deste ambiente não está pronta; o consentimento fica indisponível até ela ser concluída.',
  unsafe_redirect: 'A API devolveu um endereço de volta inesperado. Por segurança, o navegador não sai do backoffice.',
} as const;

/** Complemento da conta bloqueada nesta tela: o bloqueio não encerra o pedido. */
const LOCKED_REQUEST_STILL_OPEN = 'O pedido segue aberto até vencer, e Negar continua disponível.';

/** Desfechos que encerram o pedido nesta tela: os botões não reabrem. */
const TERMINAL_KINDS = new Set<ConsentFailureKind>([
  'invalid_client',
  'invalid_redirect_uri',
  'request_not_yours',
  'reauth',
  'session',
  'request_not_pending',
  'unavailable',
  'unsafe_redirect',
  'terminal',
]);

/** Recusas sem mensagem própria que também encerram o pedido nesta tela. */
const TERMINAL_CODES = new Set([
  `${CODE_PREFIX}approver_not_allowed`,
  `${CODE_PREFIX}client_not_certified`,
  'platform.agent_management.account_not_allowed',
]);

export function isTerminalFailure(failure: ConsentFailure): boolean {
  return TERMINAL_KINDS.has(failure.kind);
}

/** Recusas do step-up e do freio de segundo fator: o Admin tenta de novo com o próximo código. */
const STEP_UP_CODES = new Set([STEP_UP_REQUIRED, STEP_UP_INVALID, STEP_UP_RATE_LIMITED, ATTEMPTS_IN_FLIGHT]);

/**
 * Classifica a recusa da API pelo status e pelo `errorCode` do ProblemDetails. `stage` diz de onde
 * veio a recusa: do pedido (`POST agent-authorizations`, que exige login de até 15 min) ou de uma
 * decisão (o approve, que exige o step-up por TOTP e uma sessão que não veio de recovery code).
 */
export function classifyConsentFailure(err: unknown, stage: 'request' | 'decision' = 'request'): ConsentFailure {
  const status = problemStatus(err);
  const code = problemErrorCode(err);
  const failure = (kind: Exclude<ConsentFailureKind, 'terminal' | 'retryable' | 'step_up'>): ConsentFailure => ({
    kind,
    message: FAILURE_MESSAGES[kind],
  });

  if (status === 404 && !code) return failure('unavailable');
  if (status === 503 || code === `${CODE_PREFIX}misconfigured`) return failure('misconfigured');
  if (status === 401) return failure('session');
  if (status === 403) {
    const data = err instanceof AxiosError ? (err.response?.data as { reauthRequired?: unknown } | undefined) : undefined;
    if (code === `${CODE_PREFIX}reauth_required` || data?.reauthRequired === true) {
      return stage === 'decision' ? { kind: 'reauth', message: FAILURE_MESSAGES.reauth_approval } : failure('reauth');
    }
  }
  // O 423 da conta bloqueada por falhas de segundo fator (login ou step-up): a tela mostra o
  // bloqueio, sem mandar ao login, que também recusaria até o fim do bloqueio. A espera é a que a
  // API informou (`retryAfter` ou `Retry-After`), como nas telas de login e nas outras ações com
  // step-up, e o complemento lembra que o pedido continua aberto.
  if (code === ACCOUNT_LOCKED) {
    const lock = authLockMessage(err);
    return lock
      ? { kind: 'locked', message: `${lock} ${LOCKED_REQUEST_STILL_OPEN}` }
      : failure('locked');
  }
  // O step-up recusado fica no modal; se chegar aqui, não encerra o pedido: o próximo código vale.
  if (code && STEP_UP_CODES.has(code)) return { kind: 'step_up', message: describeError(err) };
  switch (code) {
    case `${CODE_PREFIX}invalid_client`:
      return failure('invalid_client');
    case `${CODE_PREFIX}invalid_redirect_uri`:
      return failure('invalid_redirect_uri');
    case `${CODE_PREFIX}mcp_disabled`:
      return failure('mcp_disabled');
    case `${CODE_PREFIX}request_not_yours`:
      return failure('request_not_yours');
    case `${CODE_PREFIX}request_not_pending`:
    case `${CODE_PREFIX}request_expired`:
      return failure('request_not_pending');
    case `${CODE_PREFIX}catalog_changed`:
      return failure('catalog_changed');
    case `${CODE_PREFIX}concurrent_requests`:
    case `${CODE_PREFIX}write_not_prepared`:
      // Dois pedidos pendentes (ambos recusados) ou a escrita que deixou de estar preparada: o
      // pedido acabou nesta tela; o texto do servidor diz o que fazer.
      return { kind: 'terminal', message: describeError(err) };
  }
  // Os demais (nível fora do concedível, recertificação vencida, conta do bootstrap, rede) mostram
  // o texto do servidor, que é fixo e em pt-BR, ou a mensagem genérica.
  return {
    kind: (code && TERMINAL_CODES.has(code)) || status === 403 ? 'terminal' : 'retryable',
    message: describeError(err),
  };
}

/** A sessão precisa ser refeita: a query volta ao `sessionStorage` para o login retornar a ela. */
export function needsSignIn(failure: ConsentFailure): boolean {
  return failure.kind === 'reauth' || failure.kind === 'session';
}

/**
 * A `redirectUrl` da API, conferida antes de sair: só `http` ou `https` (o `http` é o loopback do
 * ambiente local) e, quando a tela já mostrou o pedido, só para o host de volta dele. Devolve a URL
 * como veio, ou nulo.
 */
export function checkedRedirect(url: unknown, expectedHost?: string): string | null {
  if (typeof url !== 'string') return null;
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }
  if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return null;
  if (expectedHost !== undefined && parsed.host !== expectedHost.toLowerCase()) return null;
  return url;
}

/**
 * Para onde a tela devolve o navegador: o Hermes, o Claude Code do operador (loopback) ou o conector
 * do claude.ai do operador (change platform-mcp-operator-access, OA-D12).
 */
export type ConsentClient = 'hermes' | 'claude_code' | 'claude_web';

const CLIENT_NAMES: Record<ConsentClient, string> = {
  hermes: 'Hermes',
  claude_code: 'Claude Code',
  claude_web: 'claude.ai',
};

export function consentClientName(client: ConsentClient): string {
  return CLIENT_NAMES[client];
}

/** Motivo do erro que volta ao cliente (códigos da RFC 6749), em pt-BR. */
export function redirectErrorMessage(error: string, client: ConsentClient = 'hermes'): string {
  const name = CLIENT_NAMES[client];
  const reasons: Record<string, string> = {
    invalid_request: `o pedido do ${name} veio incompleto ou fora do formato`,
    invalid_target: 'o recurso pedido não é este servidor',
    invalid_scope: 'o pedido traz um escopo desconhecido',
    unsupported_response_type: 'o tipo de resposta pedido não é suportado',
    unauthorized_client: 'o perfil não pode pedir concessão agora',
    access_denied: 'o pedido foi negado',
  };
  const reason = reasons[error] ?? 'o pedido foi recusado';
  return `O pedido não pôde ser aceito: ${reason}. Voltando ao ${name}…`;
}

/** O caminho da volta de loopback do Claude Code (`PlatformMcp:Operator:LoopbackRedirectPath`). */
export const PERSONAL_LOOPBACK_PATH = '/callback';
/** A volta fixa do conector personalizado do claude.ai (`PlatformAgentClientRules.WebConnectorRedirectUri`). */
export const CLAUDE_WEB_CALLBACK = 'https://claude.ai/api/mcp/auth_callback';

const CLAUDE_WEB_HOST = 'claude.ai';
const CLAUDE_WEB_PATH = '/api/mcp/auth_callback';
const LOOPBACK_HOSTS = new Set(['localhost', '127.0.0.1']);

/** O destino da volta de uma URL da lista fechada do OA-D12, ou nulo fora dela. */
function personalTargetOf(parsed: URL): PersonalRedirectTarget | null {
  if (parsed.username || parsed.password || parsed.hash) return null;
  if (
    parsed.protocol === 'http:' &&
    LOOPBACK_HOSTS.has(parsed.hostname) &&
    parsed.port !== '' &&
    parsed.pathname === PERSONAL_LOOPBACK_PATH
  ) {
    return 'loopback';
  }
  if (
    parsed.protocol === 'https:' &&
    parsed.host === CLAUDE_WEB_HOST &&
    parsed.pathname === CLAUDE_WEB_PATH
  ) {
    return 'claude_web';
  }
  return null;
}

/**
 * A `redirectUrl` da variante pessoal, conferida antes de sair (OA-D12): só a lista fechada que a API
 * aceita, o loopback do Claude Code (`http://localhost:<porta>/callback` ou
 * `http://127.0.0.1:<porta>/callback`, RFC 8252, §7.3) e a volta do claude.ai
 * (`https://claude.ai/api/mcp/auth_callback`). A tela nunca monta a URL: só confere a que a API
 * devolveu. Quando o pedido já foi mostrado, ela tem de ser do destino (`redirectTarget`) e do host
 * de volta (com a porta) dele. Devolve a URL como veio, ou nulo.
 */
export function checkedPersonalRedirect(
  url: unknown,
  expected: { host?: string; target?: PersonalRedirectTarget | null } = {},
): string | null {
  if (typeof url !== 'string') return null;
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }
  const target = personalTargetOf(parsed);
  if (!target) return null;
  if (expected.target && target !== expected.target) return null;
  if (expected.host !== undefined && parsed.host !== expected.host.toLowerCase()) return null;
  return url;
}

/**
 * O cliente da volta de uma `redirectUrl` pessoal já conferida (para o texto "Voltando ao …"): o
 * claude.ai pela volta dele, o Claude Code no resto.
 */
export function personalClientOf(url: string): ConsentClient {
  try {
    return personalTargetOf(new URL(url)) === 'claude_web' ? 'claude_web' : 'claude_code';
  } catch {
    return 'claude_code';
  }
}

/**
 * O destino do pedido pessoal mostrado: o `redirectTarget` do servidor e, na API anterior a ele,
 * o host de volta (só o claude.ai tem host fixo).
 */
export function personalTargetOfConsent(
  consent: Pick<AgentAuthorizationConsent, 'redirectTarget' | 'redirectHost'>,
): PersonalRedirectTarget {
  if (consent.redirectTarget === 'claude_web' || consent.redirectTarget === 'loopback') return consent.redirectTarget;
  return consent.redirectHost.toLowerCase() === CLAUDE_WEB_HOST ? 'claude_web' : 'loopback';
}

const PERSONAL_TARGET_LABELS: Record<PersonalRedirectTarget, string> = {
  loopback: 'Claude Code neste computador',
  claude_web: 'claude.ai (conector web)',
};

/** O destino em pt-BR, como a tela de consentimento o mostra. */
export function personalTargetLabel(target: PersonalRedirectTarget): string {
  return PERSONAL_TARGET_LABELS[target];
}

/** O cliente da concessão pessoal pelo destino. */
export function personalClient(target: PersonalRedirectTarget): ConsentClient {
  return target === 'claude_web' ? 'claude_web' : 'claude_code';
}

const ENVIRONMENT_LABELS: Record<string, string> = {
  local: 'local',
  stg: 'staging',
  prd: 'produção',
};

export function environmentLabel(tag: string): string {
  return ENVIRONMENT_LABELS[tag] ?? tag;
}

export const EXCLUSION_REASON_LABELS: Record<AgentAuthorizationExclusionReason, string> = {
  above_ceiling: 'acima do teto do perfil',
  not_allowed_in_environment: 'não liberado neste ambiente',
  offline_access_discarded: 'descartado: a renovação vem da própria concessão, sem offline_access',
};

export function exclusionLabel(reason: string): string {
  return EXCLUSION_REASON_LABELS[reason as AgentAuthorizationExclusionReason] ?? 'fora do concedível';
}

/** O que um escopo do MCP libera; nulo para um escopo fora da lista (como `offline_access`). */
export function scopeDescription(scope: string): string | null {
  return (SCOPE_DESCRIPTIONS as Record<string, string>)[scope] ?? null;
}

/** Data e hora com o dia da semana, no fuso do navegador. */
export function formatConsentDateTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('pt-BR', {
    weekday: 'long',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Os níveis que o Admin pode escolher: cada nível é o conjunto cumulativo (`metrics`, depois
 * `stores`, depois `billing`), e só entra se todos os escopos dele estão no concedível. O maior é o
 * que vem marcado; nenhum fica acima do concedível.
 */
export function grantableLevels(consent: Pick<AgentAuthorizationConsent, 'grantableScopes'>): number[] {
  const grantable = new Set<string>(consent.grantableScopes.map((s) => s.scope));
  const levels: number[] = [];
  for (let level = 1; level <= AGENT_SCOPES.length; level++) {
    if (scopesUpToLevel(level).every((scope) => grantable.has(scope))) levels.push(level);
  }
  return levels;
}

// =============================================================================
//  Escrita preparada (change platform-mcp-writes, tarefa 1.18)
// =============================================================================

/** `<família>.write` contém o `<família>.propose` (o fecho que a API confere). */
export const WRITE_CONTAINMENT: Readonly<Partial<Record<AgentWriteScope, AgentWriteScope>>> = {
  'platform.mcp.trial.write': 'platform.mcp.trial.propose',
  'platform.mcp.concessions.write': 'platform.mcp.concessions.propose',
};

export const FULL_CONTROL_SCOPE: AgentWriteScope = 'platform.mcp.full';

/** Escrita direta e controle total pedem passkey; só proposta aceita o código do autenticador (W1). */
export function requiresPasskey(scope: string): boolean {
  return scope.startsWith('platform.mcp.') && !scope.endsWith('.propose') && !scope.endsWith('.read');
}

/** Algum escopo pede passkey. */
export function anyRequiresPasskey(scopes: readonly string[]): boolean {
  return scopes.some(requiresPasskey);
}

/**
 * Marca ou desmarca um escopo de escrita mantendo o fecho: marcar o `write` marca o `propose` da
 * família; desmarcar o `propose` desmarca o `write`. Só escopos de `allowed` (os concedíveis), na
 * ordem deles.
 */
export function toggleWriteScope(
  selected: readonly AgentWriteScope[],
  scope: AgentWriteScope,
  checked: boolean,
  allowed: readonly AgentWriteScope[],
): AgentWriteScope[] {
  const next = new Set(selected);
  if (checked) {
    next.add(scope);
    const contained = WRITE_CONTAINMENT[scope];
    if (contained) next.add(contained);
  } else {
    next.delete(scope);
    for (const [container, contained] of Object.entries(WRITE_CONTAINMENT)) {
      if (contained === scope) next.delete(container as AgentWriteScope);
    }
  }
  return allowed.filter((s) => next.has(s));
}

/** Por que o controle total preparado não é oferecido nesta tela. */
export const FULL_CONTROL_REFUSALS: Record<string, string> = {
  profile_not_allowed: 'Este perfil não pode ter controle total neste ambiente.',
  intent_too_old:
    'O pedido chegou mais de 5 minutos depois da preparação. Prepare de novo em Agentes de IA e recomece pelo Hermes logo em seguida.',
  nothing_grantable: 'Nenhum escopo de escrita está liberado neste ambiente: não há o que entregar no controle total.',
};

export function fullControlRefusalLabel(refusal: string | null): string {
  return (refusal && FULL_CONTROL_REFUSALS[refusal]) || 'Controle total indisponível neste pedido.';
}

/**
 * O que o agente nunca faz, nem com controle total (lista proibida do desenho): mostrado na
 * preparação e no consentimento.
 */
export const NEVER_DOES: readonly string[] = [
  'criar, ampliar ou prorrogar ordem de serviço, ou aprovar proposta',
  'mexer em conta de operador, delegação, escopo ou consentimento',
  'mudar preço, cancelar assinatura, desativar ou reativar loja',
  'apagar nota, conciliar ajuste, exportar ou apagar dado pessoal',
  'ler contato do dono ou dado de consumidor final',
];

/** O grupo de cada escopo de escrita na preparação. */
export function writeScopeGroup(scope: string): 'propose' | 'direct' | 'full' {
  if (scope === FULL_CONTROL_SCOPE) return 'full';
  return scope.endsWith('.propose') ? 'propose' : 'direct';
}

/** Segundos em texto curto: "45 s" ou "3 min 20 s". */
export function formatSeconds(total: number | null): string {
  if (total === null || Number.isNaN(total)) return '—';
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return minutes > 0 ? `${minutes} min ${seconds} s` : `${seconds} s`;
}
